import { useEffect, useState, useCallback, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import styles from './Financeiro.module.css';

// ── Ícones SVG (substituem os emojis; herdam a cor via currentColor) ──
const svgFin = { width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
const FIcon = {
  wallet: (p) => (<svg {...svgFin} {...p}><path d="M19 7V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" /><path d="M21 12a2 2 0 0 0-2-2h-5a2 2 0 0 0 0 4h5a2 2 0 0 0 2-2z" /></svg>),
  download: (p) => (<svg {...svgFin} {...p}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>),
  alert: (p) => (<svg {...svgFin} {...p}><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /><path d="M12 9v4M12 17h.01" /></svg>),
  calendar: (p) => (<svg {...svgFin} {...p}><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>),
  check: (p) => (<svg {...svgFin} {...p}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><path d="M22 4 12 14.01l-3-3" /></svg>),
  refresh: (p) => (<svg {...svgFin} {...p}><path d="M3 12a9 9 0 0 1 15-6.7L21 8M21 3v5h-5M21 12a9 9 0 0 1-15 6.7L3 16M3 21v-5h5" /></svg>),
  card: (p) => (<svg {...svgFin} {...p}><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" /></svg>),
  chart: (p) => (<svg {...svgFin} {...p}><path d="M3 3v18h18M7 14l3-4 3 3 4-6" /></svg>),
  close: (p) => (<svg {...svgFin} {...p}><path d="M18 6 6 18M6 6l12 12" /></svg>),
  search: (p) => (<svg {...svgFin} {...p}><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>),
  whatsapp: (p) => (<svg {...svgFin} {...p}><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8z" /></svg>),
};

// Monta link wa.me a partir de um telefone BR + mensagem
function linkWhatsApp(fone, msg) {
    if (!fone) return null;
    let d = String(fone).replace(/\D/g, '');
    if (!d) return null;
    if (d.length <= 11) d = '55' + d;          // sem DDI → assume Brasil
    return `https://wa.me/${d}?text=${encodeURIComponent(msg)}`;
}

// ─── Helpers ────────────────────────────────────────────────────────────
const fmtBRL = (v) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const fmtInt = (v) => new Intl.NumberFormat('pt-BR').format(v || 0);
const fmtDate = (val) => {
    if (!val) return '—';
    const ymd = typeof val === 'string' ? val.split('T')[0] : val;
    const [y, m, d] = ymd.split('-');
    if (!y || !m || !d) return ymd;
    return `${d}/${m}/${y}`;
};
function csvEscape(v) {
    if (v == null) return '';
    const s = String(v);
    return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function downloadCSV(filename, header, rows) {
    const csv = [header, ...rows].map(r => r.map(csvEscape).join(';')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ─── KpiCard ────────────────────────────────────────────────────────────
function KpiCard({ titulo, valor, sub, cor, icone, onClick, carregando, delta }) {
    const deltaCls =
        delta == null ? styles.deltaFlat :
        delta > 0 ? styles.deltaUp :
        delta < 0 ? styles.deltaDown : styles.deltaFlat;
    const deltaText = delta == null ? '' : (delta > 0 ? `+${delta}%` : `${delta}%`);
    return (
        <div
            className={`${styles.kpiCard} ${styles[cor] || ''}`}
            onClick={onClick}
            style={onClick ? { cursor: 'pointer' } : undefined}
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
        >
            <div className={styles.kpiIcon}>{icone}</div>
            <div className={styles.kpiInfo}>
                <span className={styles.kpiLabel}>{titulo}</span>
                {carregando
                    ? <span className={styles.skeleton} />
                    : <span className={styles.kpiValor}>
                        {valor}
                        {delta != null && <span className={`${styles.delta} ${deltaCls}`}>{deltaText}</span>}
                      </span>}
                {sub && <span className={styles.kpiSub}>{sub}</span>}
            </div>
        </div>
    );
}

// ─── SearchBox ──────────────────────────────────────────────────────────
function SearchBox({ onSelect }) {
    const [q, setQ] = useState('');
    const [results, setResults] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const timerRef = useRef(null);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (q.trim().length < 2) { setResults(null); return; }
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(async () => {
            setCarregando(true);
            try {
                const r = await api.get(`/api/financeiro/buscar-cliente?q=${encodeURIComponent(q.trim())}`);
                setResults(r.data?.data || []);
            } catch { setResults([]); }
            finally { setCarregando(false); }
        }, 300);
        return () => clearTimeout(timerRef.current);
    }, [q]);

    useEffect(() => {
        const handler = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setResults(null);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
        <div className={styles.searchWrap} ref={wrapRef}>
            <input
                className={styles.searchInput}
                placeholder="Buscar cliente por nome ou CPF/CNPJ…"
                value={q}
                onChange={e => setQ(e.target.value)}
            />
            {results && (
                <div className={styles.searchResults}>
                    {carregando && <div className={styles.searchItem}><span className={styles.searchItemMeta}>Buscando…</span></div>}
                    {!carregando && results.length === 0 && (
                        <div className={styles.searchItem}><span className={styles.searchItemMeta}>Nenhum cliente encontrado</span></div>
                    )}
                    {results.map(r => (
                        <div
                            key={r.contatoId}
                            className={styles.searchItem}
                            onClick={() => { onSelect(r); setResults(null); setQ(''); }}
                        >
                            <span className={styles.searchItemNome}>{r.nome || '—'}</span>
                            <span className={styles.searchItemMeta}>
                                {r.documento || 'sem documento'} • {r.contasQtd} contas
                                {r.valorVencido > 0 && (
                                    <> • <span className={styles.searchItemValor}>{fmtBRL(r.valorVencido)} vencido</span></>
                                )}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── Modal Devedor ──────────────────────────────────────────────────────
function ModalDevedor({ contatoId, onClose }) {
    const [data, setData] = useState(null);
    const [erro, setErro] = useState(null);

    useEffect(() => {
        if (!contatoId) return;
        setData(null); setErro(null);
        api.get(`/api/financeiro/devedor/${contatoId}`)
            .then(r => setData(r.data?.data))
            .catch(e => setErro(e.message || 'Erro ao carregar'));
    }, [contatoId]);

    useEffect(() => {
        const onEsc = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onEsc);
        return () => document.removeEventListener('keydown', onEsc);
    }, [onClose]);

    if (!contatoId) return null;

    const exportar = () => {
        if (!data?.contas) return;
        const nome = (data.contato?.contato_nome || data.contato?.nome || `cliente-${contatoId}`).replace(/\s+/g, '_');
        downloadCSV(
            `contas_${nome}.csv`,
            ['Vencimento', 'Valor', 'Status', 'Dias atraso', 'Forma', 'Origem', 'Emissão'],
            data.contas.map(c => [
                fmtDate(c.vencimento), c.valor.toFixed(2).replace('.', ','),
                c.status === 'vencida' ? 'Vencida' : 'A vencer',
                c.diasAtraso > 0 ? c.diasAtraso : '—',
                c.formaPgto, c.origem || '—', fmtDate(c.dataEmissao)
            ])
        );
    };

    const fone = data?.contato?.celular || data?.contato?.telefone;
    const cobrarWhatsApp = () => {
        if (!data) return;
        const nomeCompleto = data.contato?.contato_nome || data.contato?.nome || '';
        const primeiro = (String(nomeCompleto).trim().split(' ')[0]) || 'tudo bem';
        const t = data.totais || {};
        const temVencido = (t.valorVencidas || 0) > 0;
        const qtd = temVencido ? t.qtdVencidas : t.qtdTotal;
        const valor = fmtBRL(temVencido ? t.valorVencidas : t.valorTotal);
        const msg =
            `Olá ${primeiro}, tudo bem? Aqui é da Gringão Semijoias. ` +
            `Identificamos ${qtd} título(s) ${temVencido ? 'vencido(s)' : 'em aberto'} com a gente, somando ${valor}. ` +
            `Conseguimos acertar isso? Qualquer dúvida ou pra combinar o pagamento, é só me chamar por aqui. 🙏`;
        const url = linkWhatsApp(fone, msg);
        if (url) window.open(url, '_blank', 'noopener');
    };

    return (
        <div className={styles.modalBackdrop} onClick={onClose}>
            <div className={styles.modal} onClick={e => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <div>
                        <h2 className={styles.modalTitulo}>
                            {data?.contato?.contato_nome || data?.contato?.nome || 'Carregando…'}
                        </h2>
                        {data?.contato && (
                            <div className={styles.modalSubtitulo}>
                                {data.contato.contato_documento || 'sem documento'}
                                {data.contato.contato_tipo === 'F' ? ' • PF' : data.contato.contato_tipo === 'J' ? ' • PJ' : ''}
                            </div>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        {data && fone && (
                            <button
                                onClick={cobrarWhatsApp}
                                title={`Cobrar no WhatsApp (${fone})`}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: '#16a34a', color: '#fff', border: 'none', padding: '9px 14px', borderRadius: 8, fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}
                            >
                                <FIcon.whatsapp width={16} height={16} /> Cobrar no WhatsApp
                            </button>
                        )}
                        {data && !fone && (
                            <span style={{ color: '#64748b', fontSize: '0.78rem', alignSelf: 'center' }} title="Sem telefone cadastrado no Bling">sem telefone</span>
                        )}
                        {data?.contas?.length > 0 && (
                            <button className={styles.btnSecundario} onClick={exportar} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><FIcon.download width={15} height={15} /> CSV</button>
                        )}
                        <button className={styles.modalClose} onClick={onClose} aria-label="Fechar" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><FIcon.close width={20} height={20} /></button>
                    </div>
                </div>

                {erro && <div className={styles.erro}>{erro}</div>}
                {!data && !erro && <div className={styles.placeholder}>Carregando contas…</div>}

                {data && (
                    <>
                        <div className={styles.modalKpis}>
                            <div className={styles.modalKpi}>
                                <div className="label">Total Aberto</div>
                                <div className="valor">{fmtBRL(data.totais.valorTotal)}</div>
                                <span className={styles.kpiSub}>{data.totais.qtdTotal} contas</span>
                            </div>
                            <div className={styles.modalKpi} style={{ borderLeft: '3px solid #ef4444' }}>
                                <div className="label" style={{ color: '#ef4444' }}>Vencido</div>
                                <div className="valor" style={{ color: '#ef4444' }}>{fmtBRL(data.totais.valorVencidas)}</div>
                                <span className={styles.kpiSub}>{data.totais.qtdVencidas} contas</span>
                            </div>
                            <div className={styles.modalKpi} style={{ borderLeft: '3px solid #f59e0b' }}>
                                <div className="label" style={{ color: '#f59e0b' }}>A Vencer</div>
                                <div className="valor" style={{ color: '#f59e0b' }}>{fmtBRL(data.totais.valorAVencer)}</div>
                                <span className={styles.kpiSub}>{data.totais.qtdAVencer} contas</span>
                            </div>
                        </div>

                        {data.contas.length === 0 ? (
                            <div className={styles.placeholder}>🎉 Sem contas em aberto</div>
                        ) : (
                            <table className={styles.tabela}>
                                <thead>
                                    <tr>
                                        <th>Vencimento</th>
                                        <th style={{ textAlign: 'right' }}>Valor</th>
                                        <th style={{ textAlign: 'center' }}>Status</th>
                                        <th>Forma</th>
                                        <th>Origem</th>
                                        <th>Boleto/PIX</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.contas.map(c => {
                                        const grave = c.diasAtraso > 90;
                                        return (
                                            <tr key={c.id}>
                                                <td>{fmtDate(c.vencimento)}</td>
                                                <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtBRL(c.valor)}</td>
                                                <td style={{ textAlign: 'center' }}>
                                                    {c.status === 'vencida' ? (
                                                        <span className={grave ? styles.atrasoGrave : c.diasAtraso > 30 ? styles.atrasoMedio : styles.atrasoLeve}>
                                                            Vencida {c.diasAtraso}d
                                                        </span>
                                                    ) : (
                                                        <span style={{ color: '#22c55e' }}>
                                                            {c.diasAtraso === 0 ? 'Hoje' : `Em ${-c.diasAtraso}d`}
                                                        </span>
                                                    )}
                                                </td>
                                                <td>{c.formaPgto}</td>
                                                <td>{c.origem || '—'}</td>
                                                <td>
                                                    {c.linkBoleto && <a className={styles.linkBoleto} href={c.linkBoleto} target="_blank" rel="noreferrer">Boleto</a>}
                                                    {c.linkBoleto && c.linkPix && ' • '}
                                                    {c.linkPix && <a className={styles.linkPix} href={c.linkPix} target="_blank" rel="noreferrer">PIX</a>}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

// ─── Página ─────────────────────────────────────────────────────────────
export default function Financeiro() {
    const { hasPermission, loading: authLoading } = useAuth();

    const [resumo, setResumo]               = useState(null);
    const [inadimplencia, setInadimplencia] = useState(null);
    const [aReceber30d, setAReceber30d]     = useState(null);
    const [aReceberDetalhado, setAReceberDetalhado] = useState(null);
    const [recebPorForma, setRecebPorForma] = useState(null);
    const [topClientes, setTopClientes]     = useState(null);
    const [comparativo, setComparativo]     = useState(null);
    const [alertas, setAlertas]             = useState(null);
    const [histMensal, setHistMensal]       = useState(null);
    const [histAnual, setHistAnual]         = useState(null);
    const [faixaAtraso, setFaixaAtraso]     = useState(null);

    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState(null);
    const [aba, setAba] = useState('inadimplencia');
    const [modalContatoId, setModalContatoId] = useState(null);

    const carregar = useCallback(async () => {
        setCarregando(true); setErro(null);
        try {
            const [r1, r2, r3, rC] = await Promise.all([
                api.get('/api/financeiro/resumo'),
                api.get('/api/financeiro/inadimplencia'),
                api.get('/api/financeiro/a-receber-30d'),
                api.get('/api/financeiro/recebido-mes-comparativo')
            ]);
            setResumo(r1.data?.data || null);
            setInadimplencia(r2.data?.data || null);
            setAReceber30d(r3.data?.data || null);
            setComparativo(rC.data?.data || null);
        } catch (e) {
            setErro('Não foi possível carregar dados financeiros. ' + (e.response?.data?.message || e.message));
        } finally {
            setCarregando(false);
        }
    }, []);

    const carregarFormas = useCallback(async () => {
        if (recebPorForma) return;
        try {
            const r = await api.get('/api/financeiro/recebidos-por-forma');
            setRecebPorForma(r.data?.data || null);
        } catch { /* silencia */ }
    }, [recebPorForma]);

    const carregarTopClientes = useCallback(async () => {
        if (topClientes) return;
        try {
            const r = await api.get('/api/financeiro/top-clientes-mes');
            setTopClientes(r.data?.data || []);
        } catch { /* silencia */ }
    }, [topClientes]);

    const carregarMensal = useCallback(async () => {
        if (histMensal) return;
        try {
            const r = await api.get('/api/financeiro/historico-mensal?meses=24');
            setHistMensal(r.data?.data || []);
        } catch { /* silencia */ }
    }, [histMensal]);

    const carregarAnual = useCallback(async () => {
        if (histAnual) return;
        try {
            const r = await api.get('/api/financeiro/historico-anual');
            setHistAnual(r.data?.data || []);
        } catch { /* silencia */ }
    }, [histAnual]);

    const carregarFaixa = useCallback(async () => {
        try {
            const r = await api.get('/api/financeiro/faixa-atraso');
            setFaixaAtraso(r.data?.data || []);
        } catch { /* silencia */ }
    }, []);

    const carregarAReceberDetalhado = useCallback(async () => {
        if (aReceberDetalhado) return;
        try {
            const r = await api.get('/api/financeiro/a-receber-30d-detalhado');
            setAReceberDetalhado(r.data?.data || []);
        } catch { /* silencia */ }
    }, [aReceberDetalhado]);

    const carregarAlertas = useCallback(async () => {
        if (alertas) return;
        try {
            const r = await api.get('/api/financeiro/alertas');
            setAlertas(r.data?.data || null);
        } catch { /* silencia */ }
    }, [alertas]);

    useEffect(() => {
        if (!authLoading && hasPermission('financeiro')) {
            carregar();
            carregarFaixa();
        }
    }, [authLoading, hasPermission, carregar, carregarFaixa]);

    useEffect(() => {
        if (!hasPermission('financeiro')) return;
        if (aba === 'a-vencer')    carregarAReceberDetalhado();
        if (aba === 'recebidos') { carregarFormas(); carregarTopClientes(); }
        if (aba === 'alertas')     carregarAlertas();
        if (aba === 'evolucao')  { carregarMensal(); carregarAnual(); }
    }, [aba, hasPermission, carregarAReceberDetalhado, carregarFormas, carregarTopClientes, carregarAlertas, carregarMensal, carregarAnual]);

    if (authLoading) return null;
    if (!hasPermission('financeiro')) return <Navigate to="/" replace />;

    const exportarAbaAtiva = () => {
        if (aba === 'inadimplencia' && inadimplencia?.topDevedores) {
            downloadCSV(
                'top_devedores.csv',
                ['Posição', 'Devedor', 'Documento', 'Contas', 'Valor Total', 'Atraso Máx'],
                inadimplencia.topDevedores.map((d, i) => [
                    i + 1, d.contato.nome || '—', d.contato.numeroDocumento || '',
                    d.total, d.valor.toFixed(2).replace('.', ','), d.maxAtraso
                ])
            );
        } else if (aba === 'a-vencer' && aReceberDetalhado) {
            downloadCSV(
                'a_vencer_30d.csv',
                ['Vencimento', 'Cliente', 'Documento', 'Valor', 'Forma', 'Dias até vencer'],
                aReceberDetalhado.map(c => [
                    fmtDate(c.vencimento), c.cliente.nome || '—', c.cliente.documento || '',
                    c.valor.toFixed(2).replace('.', ','), c.formaPgto, c.diasAteVencer
                ])
            );
        } else if (aba === 'alertas' && alertas) {
            downloadCSV(
                'alertas_clientes.csv',
                ['Tipo', 'Cliente', 'Documento', 'Qtd Vencidas', 'Valor Vencido', 'Atraso Máx', 'Atraso Médio'],
                [
                    ...alertas.multiVencidas.map(a => ['Multi-vencidas', a.nome || '—', a.documento || '', a.qtdVencidas, a.valorVencido.toFixed(2).replace('.', ','), a.maxAtraso, a.mediaAtraso]),
                    ...alertas.cronicos.map(a => ['Crônico (>90d)', a.nome || '—', a.documento || '', a.qtdVencidas, a.valorVencido.toFixed(2).replace('.', ','), a.maxAtraso, ''])
                ]
            );
        } else {
            alert('Nada para exportar nesta aba.');
        }
    };

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <div>
                    <h1 className={styles.titulo} style={{ display: 'flex', alignItems: 'center', gap: 10 }}><FIcon.wallet width={26} height={26} /> Financeiro</h1>
                    <p className={styles.subtitulo}>
                        Sync Bling → MySQL • atualizado 1×/dia (04:15)
                        {resumo?.ultimoSync && (
                            <span style={{ marginLeft: 8, color: '#64748b' }}>
                                · último sync: {new Date(resumo.ultimoSync).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                            </span>
                        )}
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button className={styles.btnSecundario} onClick={exportarAbaAtiva} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><FIcon.download width={16} height={16} /> Exportar CSV</button>
                    <button className={styles.btnRefresh} onClick={carregar} disabled={carregando} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <FIcon.refresh width={16} height={16} className={carregando ? styles.spinning : undefined} /> Atualizar
                    </button>
                </div>
            </header>

            {erro && <div className={styles.erro} style={{ display: 'flex', alignItems: 'center', gap: 8 }}><FIcon.alert width={16} height={16} /> {erro}</div>}

            {/* ── Toolbar busca ── */}
            <div className={styles.toolbar}>
                <SearchBox onSelect={r => setModalContatoId(r.contatoId)} />
            </div>

            {/* ── KPIs ── */}
            <section className={styles.kpiGrid}>
                <KpiCard
                    titulo="A Receber (Pendentes)"
                    valor={fmtBRL(resumo?.pendentes?.valor)}
                    sub={`${fmtInt(resumo?.pendentes?.total)} contas em aberto`}
                    cor="azul"
                    icone={<FIcon.download />}
                    carregando={carregando}
                />
                <KpiCard
                    titulo="Vencidas (Inadimplência)"
                    valor={fmtBRL(resumo?.vencidas?.valor)}
                    sub={`${fmtInt(resumo?.vencidas?.total)} contas — clique p/ ver`}
                    cor="vermelho"
                    icone={<FIcon.alert />}
                    carregando={carregando}
                    onClick={() => setAba('inadimplencia')}
                />
                <KpiCard
                    titulo="A Vencer (próx. 30 dias)"
                    valor={fmtBRL(resumo?.aVencer30d?.valor)}
                    sub={`${fmtInt(resumo?.aVencer30d?.total)} contas — clique p/ ver`}
                    cor="laranja"
                    icone={<FIcon.calendar />}
                    carregando={carregando}
                    onClick={() => setAba('a-vencer')}
                />
                <KpiCard
                    titulo="Recebido no Mês"
                    valor={fmtBRL(comparativo?.atual?.valor ?? resumo?.recebidasMes?.valor)}
                    sub={
                        comparativo?.atual?.valor > 0
                            ? `${fmtInt(comparativo?.atual?.qtd)} pagamentos`
                            : 'Loja não marca como "recebida" no Bling'
                    }
                    cor="verde"
                    icone={<FIcon.check />}
                    carregando={carregando}
                    delta={comparativo?.deltaMesAnt}
                    onClick={() => setAba('recebidos')}
                />
            </section>

            {/* ── Faixa de Atraso ── */}
            {faixaAtraso && faixaAtraso.length > 0 && (() => {
                const FAIXA_CFG = {
                    'A vencer':       { cor: '#22c55e', emoji: '📅' },
                    '1-30 dias':      { cor: '#fbbf24', emoji: '⏰' },
                    '31-90 dias':     { cor: '#f59e0b', emoji: '⚠️' },
                    '91-180 dias':    { cor: '#fb923c', emoji: '🔶' },
                    '181-365 dias':   { cor: '#ef4444', emoji: '🚨' },
                    'Mais de 1 ano':  { cor: '#991b1b', emoji: '💀' }
                };
                const total = faixaAtraso.reduce((s, f) => s + f.valor, 0);
                return (
                    <section className={styles.panel} style={{ marginBottom: 20 }}>
                        <h2 className={styles.panelTitulo}>
                            Distribuição por Faixa de Atraso
                            <span className={styles.subTotal}>{' '}— total {fmtBRL(total)}</span>
                        </h2>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
                            {faixaAtraso.map(f => {
                                const cfg = FAIXA_CFG[f.faixa] || { cor: '#64748b', emoji: '•' };
                                const pct = total > 0 ? Math.round((f.valor / total) * 100) : 0;
                                return (
                                    <div key={f.faixa} style={{
                                        background: '#0f172a', padding: 14, borderRadius: 8,
                                        borderLeft: `4px solid ${cfg.cor}`
                                    }}>
                                        <div style={{ fontSize: '0.78rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>
                                            {cfg.emoji} {f.faixa}
                                        </div>
                                        <div style={{ fontSize: '1.25rem', fontWeight: 800, color: cfg.cor, marginTop: 4 }}>
                                            {fmtBRL(f.valor)}
                                        </div>
                                        <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                                            {fmtInt(f.qtd)} contas • {pct}%
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                );
            })()}

            {/* ── Abas ── */}
            <section className={styles.abasRow}>
                <button
                    className={`${styles.aba} ${aba === 'inadimplencia' ? styles.abaAtiva : ''}`}
                    onClick={() => setAba('inadimplencia')}
                >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><FIcon.alert width={15} height={15} /> Inadimplência ({inadimplencia?.totalGeral || 0})</span>
                </button>
                <button
                    className={`${styles.aba} ${aba === 'a-vencer' ? styles.abaAtiva : ''}`}
                    onClick={() => setAba('a-vencer')}
                >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><FIcon.calendar width={15} height={15} /> A Vencer 30d ({aReceber30d?.total || 0})</span>
                </button>
                <button
                    className={`${styles.aba} ${aba === 'alertas' ? styles.abaAtiva : ''}`}
                    onClick={() => setAba('alertas')}
                >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><FIcon.alert width={15} height={15} /> Alertas</span>
                </button>
                <button
                    className={`${styles.aba} ${aba === 'recebidos' ? styles.abaAtiva : ''}`}
                    onClick={() => setAba('recebidos')}
                >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><FIcon.card width={15} height={15} /> Recebidos no mês</span>
                </button>
                <button
                    className={`${styles.aba} ${aba === 'evolucao' ? styles.abaAtiva : ''}`}
                    onClick={() => setAba('evolucao')}
                >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><FIcon.chart width={15} height={15} /> Evolução</span>
                </button>
            </section>

            {/* ── Aba Inadimplência ── */}
            {aba === 'inadimplencia' && (
                <section className={styles.panel}>
                    <h2 className={styles.panelTitulo}>
                        Top Devedores
                        {inadimplencia && (
                            <span className={styles.subTotal}>
                                {' '}— Total geral: {fmtBRL(inadimplencia.valorGeral)} em {inadimplencia.totalGeral} contas. Clique numa linha pra abrir o detalhe.
                            </span>
                        )}
                    </h2>
                    {carregando ? (
                        <div className={styles.placeholder}>Carregando...</div>
                    ) : !inadimplencia?.topDevedores?.length ? (
                        <div className={styles.placeholder}>🎉 Nenhuma conta vencida!</div>
                    ) : (
                        <table className={styles.tabela}>
                            <thead>
                                <tr>
                                    <th style={{ width: '50px' }}>#</th>
                                    <th>Devedor</th>
                                    <th>Documento</th>
                                    <th style={{ textAlign: 'center' }}>Contas</th>
                                    <th style={{ textAlign: 'right' }}>Valor Total</th>
                                    <th style={{ textAlign: 'center' }}>Atraso Máx</th>
                                </tr>
                            </thead>
                            <tbody>
                                {inadimplencia.topDevedores.map((d, i) => (
                                    <tr
                                        key={d.contato.id || i}
                                        className={styles.linhaDevedor}
                                        onClick={() => d.contato.id && setModalContatoId(d.contato.id)}
                                    >
                                        <td>{i + 1}</td>
                                        <td><strong>{d.contato.nome || '—'}</strong></td>
                                        <td>{d.contato.numeroDocumento || '—'}</td>
                                        <td style={{ textAlign: 'center' }}>{d.total}</td>
                                        <td style={{ textAlign: 'right', fontWeight: 700, color: '#ef4444' }}>
                                            {fmtBRL(d.valor)}
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <span className={d.maxAtraso > 60 ? styles.atrasoGrave : d.maxAtraso > 30 ? styles.atrasoMedio : styles.atrasoLeve}>
                                                {d.maxAtraso} dias
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </section>
            )}

            {/* ── Aba A Vencer 30d ── */}
            {aba === 'a-vencer' && (
                <>
                    <section className={styles.panel}>
                        <h2 className={styles.panelTitulo}>
                            Recebimentos Esperados por Semana — Próx. 30 dias
                            {aReceber30d && (
                                <span className={styles.subTotal}>
                                    {' '}— Total: {fmtBRL(aReceber30d.valor)} em {aReceber30d.total} contas
                                </span>
                            )}
                        </h2>
                        {!aReceber30d?.semanas?.length ? (
                            <div className={styles.placeholder}>Nenhuma conta a vencer nos próximos 30 dias</div>
                        ) : (
                            <div className={styles.semanasGrid}>
                                {aReceber30d.semanas.map(s => {
                                    const fim = new Date(s.inicio + 'T00:00:00');
                                    fim.setDate(fim.getDate() + 6);
                                    const range = `${fmtDate(s.inicio)} – ${fmtDate(fim.toISOString().split('T')[0])}`;
                                    return (
                                        <div key={s.inicio} className={styles.semanaCard}>
                                            <div className={styles.semanaRange}>{range}</div>
                                            <div className={styles.semanaValor}>{fmtBRL(s.valor)}</div>
                                            <div className={styles.semanaTotal}>{s.total} {s.total === 1 ? 'conta' : 'contas'}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </section>
                    <section className={styles.panel}>
                        <h2 className={styles.panelTitulo}>
                            Contas detalhadas
                            {aReceberDetalhado && <span className={styles.subTotal}> {' '}— {aReceberDetalhado.length} contas listadas (limite 500)</span>}
                        </h2>
                        {!aReceberDetalhado ? (
                            <div className={styles.placeholder}>⏳ Carregando contas…</div>
                        ) : aReceberDetalhado.length === 0 ? (
                            <div className={styles.placeholder}>Nenhuma conta no período</div>
                        ) : (
                            <table className={styles.tabela}>
                                <thead>
                                    <tr>
                                        <th>Vencimento</th>
                                        <th>Cliente</th>
                                        <th style={{ textAlign: 'right' }}>Valor</th>
                                        <th>Forma</th>
                                        <th>Origem</th>
                                        <th style={{ textAlign: 'center' }}>Dias</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {aReceberDetalhado.map(c => (
                                        <tr
                                            key={c.id}
                                            className={c.cliente?.id ? styles.linhaDevedor : undefined}
                                            onClick={() => c.cliente?.id && setModalContatoId(c.cliente.id)}
                                        >
                                            <td>{fmtDate(c.vencimento)}</td>
                                            <td><strong>{c.cliente.nome || '—'}</strong> <span style={{ color: '#64748b' }}>{c.cliente.documento || ''}</span></td>
                                            <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtBRL(c.valor)}</td>
                                            <td>{c.formaPgto}</td>
                                            <td>{c.origem || '—'}</td>
                                            <td style={{ textAlign: 'center', color: c.diasAteVencer <= 3 ? '#f59e0b' : '#94a3b8' }}>
                                                {c.diasAteVencer === 0 ? 'Hoje' : `${c.diasAteVencer}d`}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </section>
                </>
            )}

            {/* ── Aba Alertas ── */}
            {aba === 'alertas' && (
                <>
                    <div className={styles.banner}>
                        ℹ️ Clientes que precisam de atenção: 3+ contas vencidas ou inadimplência crônica (mais de 90 dias). Clique pra ver detalhes.
                    </div>
                    <section className={styles.panel}>
                        <h2 className={styles.panelTitulo}>
                            🔁 Multi-vencidas (3+ contas atrasadas)
                            {alertas && <span className={styles.subTotal}> {' '}— {alertas.multiVencidas.length} clientes</span>}
                        </h2>
                        {!alertas ? (
                            <div className={styles.placeholder}>⏳ Carregando…</div>
                        ) : alertas.multiVencidas.length === 0 ? (
                            <div className={styles.placeholder}>🎉 Nenhum cliente com 3+ vencidas</div>
                        ) : (
                            <table className={styles.tabela}>
                                <thead>
                                    <tr>
                                        <th>Cliente</th>
                                        <th style={{ textAlign: 'center' }}>Qtd Vencidas</th>
                                        <th style={{ textAlign: 'right' }}>Valor Vencido</th>
                                        <th style={{ textAlign: 'center' }}>Atraso Máx</th>
                                        <th style={{ textAlign: 'center' }}>Atraso Médio</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {alertas.multiVencidas.map(a => (
                                        <tr
                                            key={a.contatoId}
                                            className={styles.linhaDevedor}
                                            onClick={() => setModalContatoId(a.contatoId)}
                                        >
                                            <td>
                                                <strong>{a.nome || '—'}</strong>
                                                <div style={{ color: '#64748b', fontSize: '0.78rem' }}>{a.documento || ''}</div>
                                            </td>
                                            <td style={{ textAlign: 'center', fontWeight: 700, color: '#ef4444' }}>{a.qtdVencidas}</td>
                                            <td style={{ textAlign: 'right', fontWeight: 700, color: '#ef4444' }}>{fmtBRL(a.valorVencido)}</td>
                                            <td style={{ textAlign: 'center' }}>
                                                <span className={a.maxAtraso > 90 ? styles.atrasoGrave : styles.atrasoMedio}>{a.maxAtraso}d</span>
                                            </td>
                                            <td style={{ textAlign: 'center', color: '#94a3b8' }}>{a.mediaAtraso}d</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </section>

                    <section className={styles.panel}>
                        <h2 className={styles.panelTitulo}>
                            💀 Crônicos (atraso &gt; 90 dias)
                            {alertas && <span className={styles.subTotal}> {' '}— {alertas.cronicos.length} clientes</span>}
                        </h2>
                        {!alertas ? (
                            <div className={styles.placeholder}>⏳ Carregando…</div>
                        ) : alertas.cronicos.length === 0 ? (
                            <div className={styles.placeholder}>🎉 Nenhum atraso crônico</div>
                        ) : (
                            <table className={styles.tabela}>
                                <thead>
                                    <tr>
                                        <th>Cliente</th>
                                        <th style={{ textAlign: 'center' }}>Qtd Vencidas</th>
                                        <th style={{ textAlign: 'right' }}>Valor Vencido</th>
                                        <th style={{ textAlign: 'center' }}>Atraso Máx</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {alertas.cronicos.map(a => (
                                        <tr
                                            key={a.contatoId}
                                            className={styles.linhaDevedor}
                                            onClick={() => setModalContatoId(a.contatoId)}
                                        >
                                            <td>
                                                <strong>{a.nome || '—'}</strong>
                                                <div style={{ color: '#64748b', fontSize: '0.78rem' }}>{a.documento || ''}</div>
                                            </td>
                                            <td style={{ textAlign: 'center', fontWeight: 700, color: '#ef4444' }}>{a.qtdVencidas}</td>
                                            <td style={{ textAlign: 'right', fontWeight: 700, color: '#ef4444' }}>{fmtBRL(a.valorVencido)}</td>
                                            <td style={{ textAlign: 'center' }}>
                                                <span className={styles.atrasoGrave}>{a.maxAtraso}d</span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </section>
                </>
            )}

            {/* ── Aba Recebidos no mês ── */}
            {aba === 'recebidos' && (
                <>
                    {(recebPorForma && recebPorForma.formas?.length === 0) && (
                        <div className={`${styles.banner} ${styles.bannerAviso}`}>
                            ⚠️ A loja não marca contas como "recebidas" no Bling — o pagamento é registrado em outro sistema (Macro/Go2).
                            Por isso esta aba aparece vazia mesmo quando há recebimentos reais.
                        </div>
                    )}
                    <section className={styles.panel}>
                        <h2 className={styles.panelTitulo}>
                            Como recebemos esse mês (por forma)
                            {recebPorForma && recebPorForma.valor > 0 && (
                                <span className={styles.subTotal}> {' '}— Total: {fmtBRL(recebPorForma.valor)} em {recebPorForma.total} pagamentos</span>
                            )}
                        </h2>
                        {!recebPorForma ? (
                            <div className={styles.placeholder}>⏳ Carregando…</div>
                        ) : !recebPorForma.formas?.length ? (
                            <div className={styles.placeholder}>Sem recebimentos marcados</div>
                        ) : (
                            <div className={styles.formasGrid}>
                                {recebPorForma.formas.map(f => {
                                    const pct = recebPorForma.valor > 0 ? Math.round((f.valor / recebPorForma.valor) * 100) : 0;
                                    return (
                                        <div key={f.label} className={styles.formaCard}>
                                            <div className={styles.formaLabel}>{f.label}</div>
                                            <div className={styles.formaValor}>{fmtBRL(f.valor)}</div>
                                            <div className={styles.formaBar}>
                                                <div className={styles.formaBarFill} style={{ width: `${pct}%` }} />
                                            </div>
                                            <div className={styles.formaSub}>{f.total} pgtos • {pct}%</div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </section>

                    <section className={styles.panel}>
                        <h2 className={styles.panelTitulo}>Top 10 clientes que mais pagaram no mês</h2>
                        {!topClientes ? (
                            <div className={styles.placeholder}>⏳ Carregando…</div>
                        ) : topClientes.length === 0 ? (
                            <div className={styles.placeholder}>Sem dados — mesma limitação do banner acima.</div>
                        ) : (
                            <table className={styles.tabela}>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Cliente</th>
                                        <th>Documento</th>
                                        <th style={{ textAlign: 'center' }}>Qtd Pgtos</th>
                                        <th style={{ textAlign: 'right' }}>Valor</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {topClientes.map((c, i) => (
                                        <tr key={c.contatoId} className={styles.linhaDevedor} onClick={() => setModalContatoId(c.contatoId)}>
                                            <td>{i + 1}</td>
                                            <td><strong>{c.nome || '—'}</strong></td>
                                            <td>{c.documento || '—'}</td>
                                            <td style={{ textAlign: 'center' }}>{c.qtd}</td>
                                            <td style={{ textAlign: 'right', fontWeight: 700, color: '#22c55e' }}>{fmtBRL(c.valor)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </section>
                </>
            )}

            {/* ── Aba Evolução (Mensal + Anual) ── */}
            {aba === 'evolucao' && (
                <>
                    <section className={styles.panel}>
                        <h2 className={styles.panelTitulo}>Faturamento Mensal (últimos 24 meses)</h2>
                        {!histMensal ? (
                            <div className={styles.placeholder}>⏳ Carregando...</div>
                        ) : histMensal.length === 0 ? (
                            <div className={styles.placeholder}>Sem dados — aguardando sync</div>
                        ) : (
                            <table className={styles.tabela}>
                                <thead>
                                    <tr>
                                        <th>Mês</th>
                                        <th style={{ textAlign: 'right' }}>Emitido</th>
                                        <th style={{ textAlign: 'right' }}>Recebido</th>
                                        <th style={{ textAlign: 'right' }}>Pendente</th>
                                        <th style={{ textAlign: 'right' }}>Cancelado</th>
                                        <th style={{ width: '200px' }}>Distribuição</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {histMensal.slice().reverse().map(m => {
                                        const [y, mo] = m.mes.split('-');
                                        const nomes = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
                                        const label = `${nomes[parseInt(mo)-1]}/${y}`;
                                        const pctRec = m.valorEmitido > 0 ? (m.valorRecebido / m.valorEmitido) * 100 : 0;
                                        const pctPend = m.valorEmitido > 0 ? (m.valorPendente / m.valorEmitido) * 100 : 0;
                                        const pctCanc = m.valorEmitido > 0 ? (m.valorCancelado / m.valorEmitido) * 100 : 0;
                                        return (
                                            <tr key={m.mes}>
                                                <td><strong>{label}</strong></td>
                                                <td style={{ textAlign: 'right' }}>{fmtBRL(m.valorEmitido)}</td>
                                                <td style={{ textAlign: 'right', color: '#22c55e' }}>{fmtBRL(m.valorRecebido)}</td>
                                                <td style={{ textAlign: 'right', color: '#f59e0b' }}>{fmtBRL(m.valorPendente)}</td>
                                                <td style={{ textAlign: 'right', color: '#ef4444' }}>{fmtBRL(m.valorCancelado)}</td>
                                                <td>
                                                    <div style={{ display: 'flex', height: 14, borderRadius: 4, overflow: 'hidden', background: '#0f172a' }}
                                                        title={`Receb ${pctRec.toFixed(0)}% • Pend ${pctPend.toFixed(0)}% • Canc ${pctCanc.toFixed(0)}%`}>
                                                        <div style={{ width: `${pctRec}%`, background: '#22c55e' }} />
                                                        <div style={{ width: `${pctPend}%`, background: '#f59e0b' }} />
                                                        <div style={{ width: `${pctCanc}%`, background: '#ef4444' }} />
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </section>

                    <section className={styles.panel}>
                        <h2 className={styles.panelTitulo}>Comparativo Anual</h2>
                        {!histAnual ? (
                            <div className={styles.placeholder}>⏳ Carregando...</div>
                        ) : histAnual.length === 0 ? (
                            <div className={styles.placeholder}>Sem dados ainda — aguardando sync</div>
                        ) : (
                            <div className={styles.semanasGrid} style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
                                {histAnual.slice().reverse().map(a => (
                                    <div key={a.ano} className={styles.semanaCard} style={{ borderLeftColor: '#3b82f6' }}>
                                        <div className={styles.semanaRange}>Ano</div>
                                        <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#60a5fa' }}>{a.ano}</div>
                                        <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: '1fr', gap: 4, fontSize: '0.82rem' }}>
                                            <div>
                                                <span style={{ color: '#94a3b8' }}>Emitido: </span>
                                                <strong style={{ color: '#e2e8f0' }}>{fmtBRL(a.valorEmitido)}</strong>
                                            </div>
                                            <div>
                                                <span style={{ color: '#22c55e' }}>✓ Recebido: </span>
                                                <strong style={{ color: '#22c55e' }}>{fmtBRL(a.valorRecebido)}</strong>
                                                <span style={{ color: '#64748b' }}> ({a.taxaRecebimento}%)</span>
                                            </div>
                                            <div>
                                                <span style={{ color: '#f59e0b' }}>⏳ Pendente: </span>
                                                <strong style={{ color: '#f59e0b' }}>{fmtBRL(a.valorPendente)}</strong>
                                            </div>
                                            <div>
                                                <span style={{ color: '#ef4444' }}>✕ Cancelado: </span>
                                                <strong style={{ color: '#ef4444' }}>{fmtBRL(a.valorCancelado)}</strong>
                                            </div>
                                            <div style={{ color: '#64748b', marginTop: 4 }}>{fmtInt(a.total)} contas</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                </>
            )}

            {/* ── Modal Devedor ── */}
            {modalContatoId && (
                <ModalDevedor
                    contatoId={modalContatoId}
                    onClose={() => setModalContatoId(null)}
                />
            )}
        </div>
    );
}
